import React, { useEffect, useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Download,
  Save,
  X,
  User,
  Phone,
  Mail,
  MapPin,
  Stethoscope
} from 'lucide-react';
import { doctorsAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';

const defaultForm = {
  name: '',
  contact: '',
  phone: '',
  address: '',
};

const Medecins = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Fetch doctors
  const fetchDoctors = async () => {
    setLoading(true);
    try {
      const res = await doctorsAPI.getDoctors();
      setDoctors(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des médecins');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Handle add/edit submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      if (editingId) {
        await doctorsAPI.updateDoctor(editingId, form);
        toast.success('Médecin modifié');
      } else {
        await doctorsAPI.createDoctor(form);
        toast.success('Médecin ajouté');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchDoctors();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (doctor) => {
    setForm({
      name: doctor.name,
      contact: doctor.contact || '',
      phone: doctor.phone || '',
      address: doctor.address || '',
    });
    setEditingId(doctor.id);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce médecin ?')) return;
    try {
      await doctorsAPI.deleteDoctor(id);
      toast.success('Médecin supprimé');
      fetchDoctors();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  const filteredDoctors = doctors.filter(doctor => {
    return String(doctor.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(doctor.contact || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(doctor.phone || '').includes(searchTerm) ||
           String(doctor.address || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const tableColumns = [
    {
      key: 'name',
      label: 'Nom',
      render: (value, doctor) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <Stethoscope className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{value}</div>
          </div>
        </div>
      )
    },
    {
      key: 'contact',
      label: 'Contact',
      render: (value) => (
        <div className="flex items-center">
          <Mail className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'phone',
      label: 'Téléphone',
      render: (value) => (
        <div className="flex items-center">
          <Phone className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm font-medium">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'address',
      label: 'Adresse',
      render: (value) => (
        <div className="flex items-center">
          <MapPin className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, doctor) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(doctor)}
          >
            Voir
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(doctor)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(doctor.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  const doctorsWithContact = doctors.filter(d => d.contact).length;
  const doctorsWithPhone = doctors.filter(d => d.phone).length;
  const doctorsWithAddress = doctors.filter(d => d.address).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Médecins</h1>
          <p className="text-neutral-600">Gérez la liste des médecins</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des médecins..."
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          <ModernButton
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            Ajouter un médecin
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Médecins</p>
              <p className="text-2xl font-bold text-neutral-900">{doctors.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Avec Contact</p>
              <p className="text-2xl font-bold text-success-600">{doctorsWithContact}</p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <Mail className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Avec Téléphone</p>
              <p className="text-2xl font-bold text-info-600">{doctorsWithPhone}</p>
            </div>
            <div className="w-12 h-12 bg-info-100 rounded-lg flex items-center justify-center">
              <Phone className="w-6 h-6 text-info-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Avec Adresse</p>
              <p className="text-2xl font-bold text-warning-600">{doctorsWithAddress}</p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Doctors Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des médecins</h3>
            <p className="text-sm text-neutral-600">{filteredDoctors.length} médecins trouvés</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={filteredDoctors}
          className="w-full"
        />
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier le médecin' : 'Ajouter un médecin'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ModernInput
              label="Nom"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Nom du médecin"
              icon={<User className="w-5 h-5" />}
              required
            />

            <ModernInput
              label="Contact"
              name="contact"
              value={form.contact}
              onChange={handleChange}
              placeholder="Email de contact"
              icon={<Mail className="w-5 h-5" />}
            />

            <ModernInput
              label="Téléphone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="Numéro de téléphone"
              icon={<Phone className="w-5 h-5" />}
            />

            <ModernInput
              label="Adresse"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Adresse complète"
              icon={<MapPin className="w-5 h-5" />}
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
          </div>
        </form>
      </ModernModal>
    </div>
  );
};

export default Medecins; 